import { useState } from "react"
import { Link } from "react-router-dom"
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  MeasuringStrategy,
  pointerWithin,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
  EyeIcon,
  ArrowRightIcon,
  TruckIcon,
  ClockIcon,
  GripVerticalIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { avatarInitials, avatarColor } from "@/lib/avatar"

// ─── Types ────────────────────────────────────────────────────────────────────

export type MilestoneKey =
  | "originArrivalAt"
  | "originLoadingAt"
  | "originDepartureAt"
  | "customsOriginArrivalAt"
  | "customsOriginDepartureAt"
  | "borderOriginArrivalAt"
  | "borderOriginDepartureAt"
  | "borderCrossingAt"
  | "borderDestinationArrivalAt"
  | "borderDestinationDepartureAt"
  | "customsDestinationArrivalAt"
  | "customsDestinationDepartureAt"
  | "destinationArrivalAt"
  | "destinationUnloadingAt"
  | "finalizedAt"

export interface MilestoneDef {
  key:   MilestoneKey
  label: string
  short: string
}

export interface ShipmentMilestoneRow {
  id:                            string
  internalId:                    number
  workLine:                      string | null
  status:                        string
  clientName:                    string | null
  productNames:                  string | null
  driverNames:                   string | null
  operatorNames:                 string | null
  firstTripMobileNumber:         number | null
  senderName:                    string | null
  senderCityName:                string | null
  senderCountryName:             string | null
  recipientName:                 string | null
  recipientCityName:             string | null
  recipientCountryName:          string | null
  scheduledOriginArrivalAt:      string | null
  deletedAt:                     string | null
  // Milestone timestamps
  originArrivalAt:               string | null
  originLoadingAt:               string | null
  originDepartureAt:             string | null
  customsOriginArrivalAt:        string | null
  customsOriginDepartureAt:      string | null
  borderOriginArrivalAt:         string | null
  borderOriginDepartureAt:       string | null
  borderCrossingAt:              string | null
  borderDestinationArrivalAt:    string | null
  borderDestinationDepartureAt:  string | null
  customsDestinationArrivalAt:   string | null
  customsDestinationDepartureAt: string | null
  destinationArrivalAt:          string | null
  destinationUnloadingAt:        string | null
  finalizedAt:                   string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d   = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Returns the index of the last completed milestone (-1 = none → "Pendiente"). */
export function getLastMilestoneIndex(
  row:        ShipmentMilestoneRow,
  milestones: MilestoneDef[],
): number {
  let last = -1
  for (let i = 0; i < milestones.length; i++) {
    if (row[milestones[i]!.key]) last = i
  }
  return last
}

/**
 * Compute the PATCH payload for moving a card to `targetIdx`.
 * - Forward: set all missing milestones from current+1 to targetIdx → NOW
 * - Backward: clear all milestones from targetIdx+1 to current → null
 * - Pendiente (targetIdx === -1): clear ALL milestones → null
 */
export function computeMilestoneUpdates(
  row:        ShipmentMilestoneRow,
  milestones: MilestoneDef[],
  targetIdx:  number,
): Record<string, string | null> {
  const currentIdx = getLastMilestoneIndex(row, milestones)
  if (targetIdx === currentIdx) return {}

  const now     = new Date().toISOString()
  const updates: Record<string, string | null> = {}

  if (targetIdx === -1) {
    // Drop on Pendiente → clear everything
    for (const m of milestones) {
      if (row[m.key]) updates[m.key] = null
    }
  } else if (targetIdx > currentIdx) {
    // Forward → fill all missing milestones up to target
    for (let i = Math.max(0, currentIdx + 1); i <= targetIdx; i++) {
      const m = milestones[i]!
      if (!row[m.key]) updates[m.key] = now
    }
  } else {
    // Backward → clear milestones after target up to current
    for (let i = targetIdx + 1; i <= currentIdx; i++) {
      const m = milestones[i]!
      if (row[m.key]) updates[m.key] = null
    }
  }

  return updates
}

// ─── Column ID helpers ────────────────────────────────────────────────────────

const PENDING_COL_ID  = "col-pending"
const TODAY_COL_ID    = "col-today"

function colIdFromIndex(i: number): string {
  return i === -1 ? PENDING_COL_ID : `col-${i}`
}

function indexFromColId(id: string): number {
  if (id === PENDING_COL_ID) return -1
  if (id === TODAY_COL_ID)   return -1
  return parseInt(id.replace("col-", ""), 10)
}

// ─── Column accent palette ────────────────────────────────────────────────────

interface ColAccent { border: string; count: string }

const PENDING_ACCENT: ColAccent = {
  border: "border-t-slate-400",
  count:  "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
}

const PLANNING_ACCENT: ColAccent = {
  border: "border-t-indigo-500",
  count:  "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
}

const ACCENTS: ColAccent[] = [
  { border: "border-t-blue-500",    count: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  { border: "border-t-sky-500",     count: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400" },
  { border: "border-t-cyan-500",    count: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400" },
  { border: "border-t-teal-500",    count: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400" },
  { border: "border-t-emerald-500", count: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  { border: "border-t-green-500",   count: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  { border: "border-t-lime-500",    count: "bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400" },
  { border: "border-t-yellow-500",  count: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
  { border: "border-t-amber-500",   count: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  { border: "border-t-orange-500",  count: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  { border: "border-t-rose-500",    count: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400" },
  { border: "border-t-pink-500",    count: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400" },
  { border: "border-t-purple-500",  count: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  { border: "border-t-violet-500",  count: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" },
]

// ─── AvatarStack ──────────────────────────────────────────────────────────────

function AvatarStack({ names, label }: { names: string; label: string }) {
  const list = names.split(", ").filter(Boolean)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex items-center gap-0.5 cursor-default" />}>
          <span className="text-[10px] text-muted-foreground mr-0.5">{label}</span>
          {list.slice(0, 2).map(name => (
            <Avatar key={name} size="sm">
              <AvatarFallback style={{ backgroundColor: avatarColor(name), color: "white" }}>
                {avatarInitials(name)}
              </AvatarFallback>
            </Avatar>
          ))}
          {list.length > 2 && (
            <span className="text-[10px] text-muted-foreground ml-0.5">+{list.length - 2}</span>
          )}
        </TooltipTrigger>
        <TooltipContent>{names}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ─── Mini progress strip ──────────────────────────────────────────────────────

function MilestoneProgressStrip({
  row,
  milestones,
}: {
  row:        ShipmentMilestoneRow
  milestones: MilestoneDef[]
}) {
  const lastIdx = getLastMilestoneIndex(row, milestones)
  return (
    <div className="flex items-center gap-0.5 flex-wrap">
      {milestones.map((m, i) => {
        const done   = !!row[m.key]
        const active = i === lastIdx
        return (
          <TooltipProvider key={m.key}>
            <Tooltip>
              <TooltipTrigger render={<span />}>
                <span
                  className={cn(
                    "block rounded-sm transition-all",
                    milestones.length <= 6 ? "h-1.5 w-3" : "h-1 w-1.5",
                    done
                      ? active
                        ? "bg-primary ring-1 ring-primary ring-offset-1 ring-offset-card"
                        : "bg-primary/70"
                      : "bg-muted-foreground/20",
                  )}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-[10px] max-w-[140px]">
                <p className="font-medium">{m.label}</p>
                <p className="text-muted-foreground">{done ? fmtDate(row[m.key]) : "Pendiente"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      })}
    </div>
  )
}

// ─── Milestone Card ───────────────────────────────────────────────────────────

function MilestoneCard({
  row,
  milestones,
  dragHandle,
  onView,
}: {
  row:        ShipmentMilestoneRow
  milestones: MilestoneDef[]
  dragHandle?: React.ReactNode
  onView:     (id: string) => void
}) {
  const lastIdx       = getLastMilestoneIndex(row, milestones)
  const currMilestone = lastIdx >= 0 ? milestones[lastIdx] : null
  const nextMilestone = lastIdx < milestones.length - 1 ? milestones[lastIdx + 1] : null

  const senderCity    = [row.senderCityName,   row.senderCountryName].filter(Boolean).join(" / ")
  const recipientCity = [row.recipientCityName, row.recipientCountryName].filter(Boolean).join(" / ")

  return (
    <div className="bg-card border rounded-lg p-2.5 flex flex-col gap-1.5 shadow-sm hover:shadow-md transition-shadow">

      {/* Header: drag handle + N° + product */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {dragHandle}
        <Link
          to={`/shipments/${row.id}`}
          className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded hover:bg-muted/70 transition-colors shrink-0"
        >
          #{row.internalId}
        </Link>
        {row.productNames && (
          <span className="inline-flex items-center rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground truncate max-w-[100px]">
            {row.productNames}
          </span>
        )}
        {row.firstTripMobileNumber != null && (
          <span className="flex items-center gap-0.5 text-[11px] font-bold text-muted-foreground ml-auto shrink-0">
            <TruckIcon className="size-3 shrink-0" />
            #{row.firstTripMobileNumber}
          </span>
        )}
      </div>

      {/* Route */}
      <div className="space-y-0.5">
        <div className="text-[11px] font-medium leading-tight truncate">
          {row.senderName ?? <span className="text-muted-foreground/40">Sin remitente</span>}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <ArrowRightIcon className="size-2.5 shrink-0" />
          <span className="truncate">{row.recipientName ?? "—"}</span>
        </div>
        {(senderCity || recipientCity) && (
          <div className="text-[10px] text-muted-foreground/50 truncate">
            {senderCity || "—"} → {recipientCity || "—"}
          </div>
        )}
      </div>

      {/* People */}
      {(row.operatorNames || row.driverNames) && (
        <div className="flex items-center gap-2 flex-wrap">
          {row.operatorNames && <AvatarStack names={row.operatorNames} label="Resp." />}
          {row.driverNames   && <AvatarStack names={row.driverNames}   label="Chofer" />}
        </div>
      )}

      {/* Current milestone */}
      {currMilestone && (
        <div className="flex items-center gap-1 text-[10px]">
          <ClockIcon className="size-2.5 text-primary shrink-0" />
          <span className="font-medium text-foreground truncate">{currMilestone.label}</span>
          <span className="tabular-nums text-muted-foreground shrink-0 ml-auto">
            {fmtDate(row[currMilestone.key])}
          </span>
        </div>
      )}

      {/* Next milestone hint */}
      {nextMilestone && (
        <div className="text-[10px] text-muted-foreground/50 truncate">
          ↳ {nextMilestone.label}
        </div>
      )}

      {/* Progress strip */}
      <MilestoneProgressStrip row={row} milestones={milestones} />

      {/* Footer */}
      <div className="flex items-center justify-end pt-1 border-t border-border/40">
        <Button variant="ghost" size="icon-sm" onClick={() => onView(row.id)} title="Ver detalle">
          <EyeIcon className="size-3" />
        </Button>
      </div>
    </div>
  )
}

// ─── DraggableCard ────────────────────────────────────────────────────────────

function DraggableCard({
  row,
  milestones,
  onView,
  renderCard,
}: {
  row:         ShipmentMilestoneRow
  milestones:  MilestoneDef[]
  onView:      (id: string) => void
  renderCard?: (row: ShipmentMilestoneRow, dragHandle: React.ReactNode) => React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id:   row.id,
    data: { row },
  })

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined

  const dragHandle = (
    <button
      className="cursor-grab active:cursor-grabbing text-muted-foreground/30 hover:text-muted-foreground/70 transition-colors touch-none shrink-0 -ml-0.5"
      aria-label="Arrastrar"
      {...attributes}
      {...listeners}
    >
      <GripVerticalIcon className="size-3.5" />
    </button>
  )

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? "opacity-30" : undefined}>
      {renderCard
        ? renderCard(row, dragHandle)
        : <MilestoneCard row={row} milestones={milestones} dragHandle={dragHandle} onView={onView} />}
    </div>
  )
}

// ─── DroppableColumn ─────────────────────────────────────────────────────────

function DroppableColumn({
  colId,
  short,
  label,
  rows,
  accent,
  milestones,
  onView,
  renderCard,
}: {
  colId:       string
  short:       string
  label:       string
  rows:        ShipmentMilestoneRow[]
  accent:      ColAccent
  milestones:  MilestoneDef[]
  onView:      (id: string) => void
  renderCard?: (row: ShipmentMilestoneRow, dragHandle: React.ReactNode) => React.ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: colId })
  const isEmpty = rows.length === 0

  // Empty columns: full width drop zone, visually compact
  if (isEmpty) {
    return (
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-col rounded-lg border border-t-2 cursor-default transition-colors duration-150",
          accent.border,
          isOver ? "bg-primary/5 ring-2 ring-primary/40 ring-inset" : "bg-muted/20",
        )}
      >
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-border/50 pointer-events-none">
          <span className="text-[10px] font-semibold leading-tight truncate opacity-40" title={label}>{short}</span>
          <span className={cn(
            "ml-1.5 shrink-0 rounded-full px-1 py-0.5 text-[10px] font-bold tabular-nums opacity-40",
            accent.count,
          )}>0</span>
        </div>
        <div className="flex-1 min-h-16" />
      </div>
    )
  }

  return (
    <div className={cn(
      "flex flex-col rounded-lg border bg-muted/30 border-t-2",
      accent.border,
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border/50">
        <span className="text-[10px] font-semibold leading-tight truncate" title={label}>{short}</span>
        <span className={cn(
          "ml-1.5 shrink-0 rounded-full px-1 py-0.5 text-[10px] font-bold tabular-nums",
          accent.count,
        )}>
          {rows.length}
        </span>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-col gap-1.5 p-1.5 overflow-y-auto max-h-[calc(100vh-200px)] min-h-[60px] rounded-b-lg transition-colors duration-150",
          isOver && "bg-primary/5 ring-1 ring-primary/30 ring-inset",
        )}
      >
        {rows.length === 0 ? (
          <p className="text-[10px] text-muted-foreground/40 text-center py-4">Sin cargas</p>
        ) : (
          rows.map(row => (
            <DraggableCard key={row.id} row={row} milestones={milestones} onView={onView} {...(renderCard ? { renderCard } : {})} />
          ))
        )}
      </div>
    </div>
  )
}

// ─── MilestoneKanban ──────────────────────────────────────────────────────────

export interface MilestoneKanbanProps {
  items:                    ShipmentMilestoneRow[]
  milestones:               MilestoneDef[]
  onView:                   (id: string) => void
  onMilestoneChange:        (id: string, updates: Record<string, string | null>) => void
  onMilestoneChangeRequest?: (id: string, updates: Record<string, string | null>) => void
  showPending?:             boolean
  renderCard?:              (row: ShipmentMilestoneRow, dragHandle: React.ReactNode) => React.ReactNode
}

export default function MilestoneKanban({
  items,
  milestones,
  onView,
  onMilestoneChange,
  onMilestoneChangeRequest,
  showPending = true,
  renderCard,
}: MilestoneKanbanProps) {
  const [activeRow, setActiveRow] = useState<ShipmentMilestoneRow | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  function handleDragStart(event: DragStartEvent) {
    const row = event.active.data.current?.row as ShipmentMilestoneRow | undefined
    setActiveRow(row ?? null)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setActiveRow(null)
    if (!over) return

    const row = active.data.current?.row as ShipmentMilestoneRow | undefined
    if (!row) return

    const targetIdx = indexFromColId(over.id as string)
    const currentIdx = getLastMilestoneIndex(row, milestones)
    const updates = computeMilestoneUpdates(row, milestones, targetIdx)
    if (Object.keys(updates).length > 0) {
      const isBackwardMove = targetIdx < currentIdx
      if (!isBackwardMove && onMilestoneChangeRequest) {
        onMilestoneChangeRequest(row.id, updates)
      } else {
        onMilestoneChange(row.id, updates)
      }
    }
  }

  // Build column data
  // First column: planificado/activo shipments with no milestone completed yet
  const pendingRows = items.filter(r =>
    getLastMilestoneIndex(r, milestones) === -1 &&
    (r.status === "en_planificacion" || r.status === "planificado" || r.status === "activo")
  )
  const columns = [
    {
      colId:  TODAY_COL_ID,
      short:  "En planificación",
      label:  "En planificación",
      rows:   pendingRows,
      accent: PLANNING_ACCENT,
    },
    ...(showPending ? [{
      colId:  PENDING_COL_ID,
      short:  "Pendiente",
      label:  "Pendiente",
      rows:   pendingRows,
      accent: PENDING_ACCENT,
    }] : []),
    ...milestones.map((m, i) => ({
      colId:  colIdFromIndex(i),
      short:  m.short,
      label:  m.label,
      rows:   items.filter(r => getLastMilestoneIndex(r, milestones) === i),
      accent: ACCENTS[i % ACCENTS.length]!,
    })),
  ]

  const totalCols   = columns.length
  // Internacional (14 cols) gets a horizontal scroll; Nacional (6 cols) uses flex
  const needsScroll = totalCols > 7
  const COL_PX      = 155 // fixed width for scrollable boards

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      autoScroll={false}
    >

      <div className={needsScroll ? "overflow-x-auto pb-3" : undefined}>
        <div
          className={cn(
            "flex gap-2 items-start",
            !needsScroll && "flex-wrap",
          )}
          style={undefined}
        >
          {columns.map(col => (
            <div
              key={col.colId}
              style={needsScroll
                ? col.rows.length === 0
                  ? { width: `${Math.round(COL_PX * 0.70)}px`, flexShrink: 0 }
                  : { width: `${COL_PX}px`, flexShrink: 0 }
                : { flex: "1 1 160px", maxWidth: "250px" }
              }
            >
              <DroppableColumn
                colId={col.colId}
                short={col.short}
                label={col.label}
                rows={col.rows}
                accent={col.accent}
                milestones={milestones}
                onView={onView}
                {...(renderCard ? { renderCard } : {})}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Floating preview while dragging */}
      <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
        {activeRow ? (
          <div className="rotate-1 shadow-2xl opacity-95 w-[220px]">
            {renderCard
              ? renderCard(activeRow, null)
              : <MilestoneCard row={activeRow} milestones={milestones} onView={onView} />}
          </div>
        ) : null}
      </DragOverlay>

    </DndContext>
  )
}
